import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getSession, SessionLogin, SessionLogout } from "../../session/session";
import AlternateEmailOutlinedIcon from "@mui/icons-material/AlternateEmailOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { defaultSession } from "../../session/lib";
import FabrioLogo from "../../assets/Fabrio-Logo.png";

import {
  FinYearDataType,
  GetFinYearResponseType,
  LoginResponseType,
} from "../../db/model";
import { GetFinancialYearAPI, LoginAPI } from "../../db/apiCalls";
import { toast } from "react-toastify";
import ErrorBoundary from "../../ErrorBoundary";

export default function Login() {
  useEffect(() => {
    userNameRef.current?.focus();
    try {
      getSession().then((sess) => {
        const data = JSON.parse(sess ? sess : JSON.stringify(defaultSession));
        if (data.isLoggedIn) {
          navigate("/work-contract-register");
        }
      });
    } catch (error) {
      SessionLogout();
      navigate("/login");
    }
    // eslint-disable-next-line
  }, []);

  const [finYearList, setFinYearList] = useState<FinYearDataType[]>([]);

  async function getFinancialYear() {
    let result = await GetFinancialYearAPI();

    let parsedFinYearResult: GetFinYearResponseType = result
      ? JSON.parse(result)
      : {
          statusCode: 0,
          message:
            "Something went wrong! Please check your internet connection.",
          success: false,
          finDatas: [],
        };

    if (parsedFinYearResult.success) {
      return parsedFinYearResult.finDatas;
    } else {
      toast.error(
        `Error getting financial year list, ${parsedFinYearResult.statusCode} : ${parsedFinYearResult.message}`
      );
      return [];
    }
  }

  useEffect(() => {
    getFinancialYear().then((response: FinYearDataType[]) => {
      setFinYearList(response);
    });
  }, []);

  const loginClickedRef = useRef<HTMLButtonElement>(null);

  const finYearRef = useRef<HTMLSelectElement>(null);
  const userNameRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);

  const navigate = useNavigate();

  async function loginClicked(e: any) {
    e.preventDefault();

    if (loginClickedRef.current) {
      loginClickedRef.current.disabled = true;
    }

    let userName = userNameRef.current?.value;
    let password = passwordRef.current?.value;
    if (
      userName === "" ||
      userName === undefined ||
      password === "" ||
      password === undefined
    ) {
      toast.warning("Please fill both fields");
      setTimeout(() => {
        if (loginClickedRef.current) {
          loginClickedRef.current.disabled = false;
        }
      }, 200);
      return false;
    } else {
      await handleLoginClick(userName, password);
    }
  }

  async function handleLoginClick(userName: string, password: string) {
    const loginResult = await LoginAPI(userName, password);

    // console.log("loginResult")
    // console.log(JSON.stringify(loginResult))

    var parsedLoginResult: LoginResponseType = loginResult
      ? JSON.parse(loginResult)
      : {
          statusCode: 400,
          message:
            "Something went wrong! Please check your internet connection.",
          success: false,
          userData: {
            userName: "",
            accessToken: "",
            userType: "",
            partyCode: "",
          },
        };

    if (parsedLoginResult.success) {
      await SessionLogin(
        parsedLoginResult.userData.userName,
        parsedLoginResult.userData.accessToken,
        parsedLoginResult.userData.userType,
        parsedLoginResult.userData.partyCode
      );

      navigate("/work-contract-register");

      setTimeout(() => {
        if (loginClickedRef.current) {
          loginClickedRef.current.disabled = false;
        }
      }, 10000);
    } else {
      toast.error(
        `Login Failed, with error ${parsedLoginResult.statusCode}: ${parsedLoginResult.message}`
      );
      setTimeout(() => {
        if (loginClickedRef.current) {
          loginClickedRef.current.disabled = false;
        }
      }, 200);
    }
  }

  return (
    <ErrorBoundary>
      <div className="bg-zinc-100 min-h-svh flex flex-row items-center dark:bg-slate-700">
        <div className="container p-0">
          {/* company image */}

          <div className="image-container mx-auto max-w-[75%] sm:max-w-[50%] md:max-w-[25%] justify-center text-center">
            <img
              src={FabrioLogo}
              alt="Ayuswasth logo"
              className="max-w-full max-h-32 mx-auto"
            />
          </div>

          <br />
          <div className="bg-white rounded-lg shadow-2xl px-8 py-6 mx-auto login-card w-3/4 sm:w-1/2 md:w-1/4 justify-center text-center dark:bg-slate-800">
            <h1 className="mb-2 text-lg dark:text-white">
              <b>Login</b>
            </h1>
            <p className="text-gray-500 text-sm mb-4 dark:text-slate-300">
              Sign In to your account
            </p>

            <form onSubmit={loginClicked}>
              <div className="relative mt-2 rounded-md shadow-sm mb-5">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-2"></div>
                <select
                  name="finYear"
                  ref={finYearRef}
                  className="block w-full rounded-md border-0 py-1.5 px-2 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                >
                  {finYearList.map((item, index) => {
                    return (
                      <option
                        key={index}
                        value={item.yearCode}
                      >{`${item.startDate} - ${item.endDate}`}</option>
                    );
                  })}
                </select>
              </div>

              <div className="relative mt-2 rounded-md shadow-sm mb-5">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-2">
                  <span className="text-gray-500 sm:text-sm text-xs">
                    <AlternateEmailOutlinedIcon fontSize="small" />
                  </span>
                </div>
                <input
                  name="userName"
                  type="text"
                  ref={userNameRef}
                  className="block w-full rounded-md border-0 py-1.5 pl-7 pr-2 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                  placeholder="UserName"
                  aria-label="UserName"
                  aria-describedby="basic-addon1"
                />
              </div>

              <div className="relative mt-2 rounded-md shadow-sm mb-5">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-2">
                  <span className="text-gray-500 sm:text-sm text-xs">
                    <LockOutlinedIcon fontSize="small" className="" />
                  </span>
                </div>
                <input
                  type="password"
                  name="password"
                  ref={passwordRef}
                  className="block w-full rounded-md border-0 py-1.5 pl-7 pr-2 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                  placeholder="Password"
                  aria-label="Username"
                />
              </div>

              <div className="grid gap-y-2 w-full text-center justify-around my-4">
                <button
                  type="submit"
                  className="primary-btn py-2 px-14 max-w-full bg-blue-600 text-white hover:bg-blue-500 rounded-lg disabled:bg-gray-400 my-auto"
                  ref={loginClickedRef}
                >
                  Login
                </button>
                {/* <Link
                type="button"
                role="link"
                className="secondary-btn py-2 px-14 max-w-full nav-link text-sm text-blue-600 border rounded-md border-blue-600 bg-white my-auto hover:bg-blue-100"
                to={"/forgot-password"}
              >
                Forgot Password?
              </Link> */}
              </div>
            </form>
          </div>
        </div>
      </div>
    </ErrorBoundary>
  );
}
